import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { apiClient } from '../lib/api-client';

const SUPPORTED_LANGUAGES = [
  'en',
  'ja',
  'es',
  'fr',
  'ko',
  'de',
  'pt',
  'tr',
  'zh-cn',
  'zh-tw',
  'it'
];
type Currency = {
  id: string;
  code: string;
  name: string;
  symbol: string;
  useDecimals: boolean;
  decimalPlaces: number;
  displayOrder: number;
  isActive: boolean;
};

type Tier = {
  id: string;
  key: string;
  name: string;
  displayName: Record<string, string>;
  isActive: boolean;
};

function CreatePlanPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(true);
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [loadingTiers, setLoadingTiers] = useState(true);

  const [formData, setFormData] = useState({
    tier: '',
    name: '',
    displayName: Object.fromEntries(SUPPORTED_LANGUAGES.map(lang => [lang, ''])),
    description: Object.fromEntries(SUPPORTED_LANGUAGES.map(lang => [lang, ''])),
    priceMonthly: {} as Record<string, number>,
    priceYearly: {} as Record<string, number>,
    currency: 'usd',
    limits: {
      // FEATURE_PLANS.md - 期間内予約投稿数上限
      monthlyPostsLimit: 10,
      // FEATURE_PLANS.md - 同時予約上限
      pendingPostsLimit: 3,
      // FEATURE_PLANS.md - 予約削除の同時上限
      pendingDeleteActionsLimit: 45,
      // FEATURE_PLANS.md - 同一時刻あたりの予約アクション上限
      actionsPerSlotLimit: 5,
      // FEATURE_PLANS.md - 予約可能期間（日）
      maxScheduleDays: 7,
      // FEATURE_PLANS.md - マークダウン記法
      markdownSupport: false,
      // FEATURE_PLANS.md - 設定時間間隔(分)
      scheduleIntervalMinutes: 10,
      // FEATURE_PLANS.md - 画像枚数/投稿
      maxImagesPerPost: 1,
      // FEATURE_PLANS.md - 画像サイズ上限
      maxImageSizeMb: 1,
      // FEATURE_PLANS.md - スレッド投稿 (boolean: 機能有効/無効)
      threadPosts: false,
      // FEATURE_PLANS.md - スレッド投稿上限 (number: 最大子投稿数)
      threadPostsLimit: 1,
      // FEATURE_PLANS.md - 動画アップロード
      videoUpload: false,
      // FEATURE_PLANS.md - 動画サイズ上限
      maxVideoSizeMb: null as number | null,
      // FEATURE_PLANS.md - 動画処理時間/月
      video_processing_minutes_per_month: null as number | null,
      // その他の制限
      maxPostLength: 300,
      storage_mb: 100,
      api_rate_limit_per_hour: 60,
      data_retention_days: 30
    } as Record<string, number | boolean | null>,
    features: Object.fromEntries(SUPPORTED_LANGUAGES.map(lang => [lang, [] as string[]])),
    displayOrder: 0,
    isActive: true,
    isVisible: true,
    isPaid: false
  });

  useEffect(() => {
    loadCurrencies();
    loadTiers();
  }, []);

  const loadTiers = async () => {
    try {
      setLoadingTiers(true);
      const data = await apiClient.getTiers({ includeInactive: false });
      setTiers(data.tiers);
    } catch (err) {
      console.error('Failed to load tiers:', err);
    } finally {
      setLoadingTiers(false);
    }
  };

  const loadCurrencies = async () => {
    try {
      setLoadingCurrencies(true);
      const data = await apiClient.getCurrencies(false); // Only active currencies
      setCurrencies(data.currencies);

      // Initialize price fields with active currencies
      const initialPrices = Object.fromEntries(data.currencies.map(c => [c.code, 0]));
      setFormData(prev => ({
        ...prev,
        priceMonthly: initialPrices,
        priceYearly: initialPrices,
        currency: data.currencies[0]?.code || 'usd'
      }));
    } catch (err) {
      console.error('Failed to load currencies:', err);
      setError('Failed to load currencies. Please refresh the page.');
    } finally {
      setLoadingCurrencies(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Validate required fields
      if (!formData.tier || !formData.name) {
        throw new Error('Tier and name are required');
      }

      if (!formData.displayName.en || !formData.description.en) {
        throw new Error('English display name and description are required');
      }

      // Convert Record format to prices array for API
      const prices = formData.isPaid
        ? Object.entries(formData.priceMonthly).map(([currencyCode, monthly]) => ({
            id: `temp_${currencyCode}`,
            currencyCode,
            priceMonthly: monthly || null,
            priceYearly: formData.priceYearly[currencyCode] || null
          }))
        : [];

      const result = await apiClient.createPlan({
        tier: formData.tier,
        name: formData.name,
        displayName: formData.displayName,
        description: formData.description,
        prices,
        limits: formData.limits,
        features: formData.features,
        displayOrder: formData.displayOrder,
        isActive: formData.isActive,
        isVisible: formData.isVisible
      });

      setSuccess(`Plan created successfully: ${result.plan.name}`);

      setTimeout(() => {
        navigate('/plans');
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create plan');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: unknown) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMultiLangChange = (
    field: 'displayName' | 'description',
    lang: string,
    value: string
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [lang]: value
      }
    }));
  };

  const handlePriceChange = (
    field: 'priceMonthly' | 'priceYearly',
    currency: string,
    value: number
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [currency]: value
      }
    }));
  };

  const handleLimitChange = (key: string, value: number | boolean | null) => {
    setFormData(prev => ({
      ...prev,
      limits: {
        ...prev.limits,
        [key]: value
      }
    }));
  };

  const handleFeatureChange = (lang: string, value: string) => {
    const features = value.split('\n').filter(f => f.trim());
    setFormData(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [lang]: features
      }
    }));
  };

  return (
    <div className='mx-auto max-w-4xl space-y-6'>
      <div className='flex items-center gap-4'>
        <button
          onClick={() => navigate('/plans')}
          className='text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300'
        >
          ← Back to Plans
        </button>
        <h1 className='text-3xl font-bold text-gray-900 dark:text-white'>Create New Plan</h1>
      </div>

      {error && (
        <div className='rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-900/30 dark:bg-red-900/20'>
          <p className='text-red-800 dark:text-red-200'>{error}</p>
        </div>
      )}

      {success && (
        <div className='rounded-md border border-green-200 bg-green-50 p-4 dark:border-green-900/30 dark:bg-green-900/20'>
          <p className='text-green-800 dark:text-green-200'>{success}</p>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className='space-y-6 rounded-lg bg-white p-6 shadow-md dark:bg-gray-800'
      >
        {/* Basic Info */}
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          <div>
            <label
              htmlFor='tier'
              className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'
            >
              Tier <span className='text-red-500'>*</span>
            </label>
            <select
              id='tier'
              value={formData.tier}
              onChange={e => handleChange('tier', e.target.value)}
              required
              className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white'
            >
              <option value=''>{loadingTiers ? 'Loading tiers...' : 'Select tier'}</option>
              {tiers.map(tier => (
                <option key={tier.key} value={tier.key}>
                  {tier.key} — {tier.displayName?.en ?? tier.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor='name'
              className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'
            >
              Internal Name <span className='text-red-500'>*</span>
            </label>
            <input
              type='text'
              id='name'
              value={formData.name}
              onChange={e => handleChange('name', e.target.value)}
              required
              className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white'
              placeholder='e.g., basic-plan'
            />
          </div>
        </div>

        {/* Display Names */}
        <div>
          <label className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'>
            Display Names <span className='text-red-500'>*</span>
          </label>
          <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
            {SUPPORTED_LANGUAGES.map(lang => (
              <div key={lang}>
                <label className='mb-1 block text-xs text-gray-600 dark:text-gray-400'>
                  {lang.toUpperCase()}
                </label>
                <input
                  type='text'
                  value={formData.displayName[lang]}
                  onChange={e => handleMultiLangChange('displayName', lang, e.target.value)}
                  required={lang === 'en'}
                  className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                  placeholder={`Display name in ${lang}`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Descriptions */}
        <div>
          <label className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'>
            Descriptions <span className='text-red-500'>*</span>
          </label>
          <div className='space-y-3'>
            {SUPPORTED_LANGUAGES.map(lang => (
              <div key={lang}>
                <label className='mb-1 block text-xs text-gray-600 dark:text-gray-400'>
                  {lang.toUpperCase()}
                </label>
                <textarea
                  value={formData.description[lang]}
                  onChange={e => handleMultiLangChange('description', lang, e.target.value)}
                  required={lang === 'en'}
                  rows={2}
                  className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                  placeholder={`Description in ${lang}`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Pricing */}
        <div>
          <div className='mb-3 flex items-center gap-3'>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>
              Pricing
            </label>
            <label className='flex items-center gap-2'>
              <input
                type='checkbox'
                checked={formData.isPaid}
                onChange={e => handleChange('isPaid', e.target.checked)}
                className='h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700'
              />
              <span className='text-sm text-gray-600 dark:text-gray-400'>Paid plan</span>
            </label>
          </div>

          {formData.isPaid && (
            <>
              {loadingCurrencies ? (
                <div className='py-4 text-center text-gray-500 dark:text-gray-400'>
                  Loading currencies...
                </div>
              ) : currencies.length === 0 ? (
                <div className='rounded-md border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-900/30 dark:bg-yellow-900/20'>
                  <p className='text-yellow-800 dark:text-yellow-200'>
                    No active currencies found. Please add currencies in the{' '}
                    <Link
                      to={{ pathname: '/settings', hash: 'currencies' }}
                      className='underline hover:text-yellow-900 dark:hover:text-yellow-100'
                    >
                      settings
                    </Link>
                    .
                  </p>
                </div>
              ) : (
                <>
                  <div className='mb-3 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'>
                    {currencies.map(currency => (
                      <div key={currency.code}>
                        <label className='mb-1 block text-xs text-gray-600 dark:text-gray-400'>
                          Monthly ({currency.code.toUpperCase()}) {currency.symbol}
                        </label>
                        <input
                          type='number'
                          value={formData.priceMonthly[currency.code] || 0}
                          onChange={e =>
                            handlePriceChange(
                              'priceMonthly',
                              currency.code,
                              parseFloat(e.target.value) || 0
                            )
                          }
                          min='0'
                          step={
                            currency.useDecimals
                              ? Math.pow(0.1, currency.decimalPlaces).toFixed(
                                  currency.decimalPlaces
                                )
                              : '1'
                          }
                          className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                          placeholder={
                            currency.useDecimals ? `0.${'0'.repeat(currency.decimalPlaces)}` : '0'
                          }
                        />
                        {!currency.useDecimals && (
                          <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                            No decimals
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'>
                    {currencies.map(currency => (
                      <div key={currency.code}>
                        <label className='mb-1 block text-xs text-gray-600 dark:text-gray-400'>
                          Yearly ({currency.code.toUpperCase()}) {currency.symbol}
                        </label>
                        <input
                          type='number'
                          value={formData.priceYearly[currency.code] || 0}
                          onChange={e =>
                            handlePriceChange(
                              'priceYearly',
                              currency.code,
                              parseFloat(e.target.value) || 0
                            )
                          }
                          min='0'
                          step={
                            currency.useDecimals
                              ? Math.pow(0.1, currency.decimalPlaces).toFixed(
                                  currency.decimalPlaces
                                )
                              : '1'
                          }
                          className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                          placeholder={
                            currency.useDecimals ? `0.${'0'.repeat(currency.decimalPlaces)}` : '0'
                          }
                        />
                        {!currency.useDecimals && (
                          <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                            No decimals
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Limits */}
        <div>
          <label className='mb-3 block text-sm font-medium text-gray-700 dark:text-gray-300'>
            Plan Limits <span className='text-red-500'>*</span>
          </label>
          <div className='space-y-4'>
            {/* Core Posting Limits - FEATURE_PLANS.md */}
            <div className='border-b pb-4 dark:border-gray-700'>
              <h3 className='mb-3 text-sm font-semibold text-gray-700 dark:text-gray-300'>
                Core Posting Limits
              </h3>
              <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                <div>
                  <label className='mb-1 block text-xs text-gray-600 dark:text-gray-400'>
                    Monthly Posts Limit (期間内予約投稿数上限)
                  </label>
                  <input
                    type='number'
                    value={formData.limits.monthlyPostsLimit as number}
                    onChange={e =>
                      handleLimitChange('monthlyPostsLimit', parseInt(e.target.value) || 0)
                    }
                    min='-1'
                    className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                  />
                  <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>-1 for unlimited</p>
                </div>

                <div>
                  <label className='mb-1 block text-xs text-gray-600 dark:text-gray-400'>
                    Pending Posts Limit (同時予約上限)
                  </label>
                  <input
                    type='number'
                    value={formData.limits.pendingPostsLimit as number}
                    onChange={e =>
                      handleLimitChange('pendingPostsLimit', parseInt(e.target.value) || 0)
                    }
                    min='-1'
                    className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                  />
                  <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>-1 for unlimited</p>
                </div>

                <div>
                  <label className='mb-1 block text-xs text-gray-600 dark:text-gray-400'>
                    Pending Delete Actions Limit (予約削除の同時上限)
                  </label>
                  <input
                    type='number'
                    value={formData.limits.pendingDeleteActionsLimit as number}
                    onChange={e =>
                      handleLimitChange(
                        'pendingDeleteActionsLimit',
                        parseInt(e.target.value) || 0
                      )
                    }
                    min='-1'
                    className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                  />
                  <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>-1 for unlimited</p>
                </div>

                <div>
                  <label className='mb-1 block text-xs text-gray-600 dark:text-gray-400'>
                    Actions Per Slot Limit (同一時刻あたりの予約アクション上限)
                  </label>
                  <input
                    type='number'
                    value={formData.limits.actionsPerSlotLimit as number}
                    onChange={e =>
                      handleLimitChange('actionsPerSlotLimit', parseInt(e.target.value) || 0)
                    }
                    min='-1'
                    className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                  />
                  <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>-1 for unlimited</p>
                </div>

                <div>
                  <label className='mb-1 block text-xs text-gray-600 dark:text-gray-400'>
                    Max Schedule Days (予約可能期間)
                  </label>
                  <input
                    type='number'
                    value={formData.limits.maxScheduleDays as number}
                    onChange={e =>
                      handleLimitChange('maxScheduleDays', parseInt(e.target.value) || 0)
                    }
                    min='1'
                    className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                  />
                  <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>Days in advance</p>
                </div>

                <div>
                  <label className='mb-1 block text-xs text-gray-600 dark:text-gray-400'>
                    Schedule Interval (設定時間間隔)
                  </label>
                  <select
                    value={formData.limits.scheduleIntervalMinutes as number}
                    onChange={e =>
                      handleLimitChange('scheduleIntervalMinutes', parseInt(e.target.value) || 10)
                    }
                    className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                  >
                    <option value={1}>1 minute</option>
                    <option value={5}>5 minutes</option>
                    <option value={10}>10 minutes</option>
                    <option value={15}>15 minutes</option>
                  </select>
                  <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                    Minimum interval between posts
                  </p>
                </div>

                <div className='flex items-center'>
                  <label className='flex items-center gap-2'>
                    <input
                      type='checkbox'
                      checked={formData.limits.threadPosts as boolean}
                      onChange={e => handleLimitChange('threadPosts', e.target.checked)}
                      className='h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700'
                    />
                    <span className='text-sm text-gray-700 dark:text-gray-300'>
                      Thread Posts (スレッド投稿)
                    </span>
                  </label>
                </div>

                {formData.limits.threadPosts && (
                  <div>
                    <label className='mb-1 block text-xs text-gray-600 dark:text-gray-400'>
                      Thread Posts Limit (最大子投稿数)
                    </label>
                    <input
                      type='number'
                      value={formData.limits.threadPostsLimit as number}
                      onChange={e =>
                        handleLimitChange('threadPostsLimit', parseInt(e.target.value) || 1)
                      }
                      min='1'
                      max='24'
                      className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                    />
                    <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                      Max child posts (1-24, AT Protocol max is 25 posts total)
                    </p>
                  </div>
                )}

                <div className='flex items-center'>
                  <label className='flex items-center gap-2'>
                    <input
                      type='checkbox'
                      checked={formData.limits.markdownSupport as boolean}
                      onChange={e => handleLimitChange('markdownSupport', e.target.checked)}
                      className='h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700'
                    />
                    <span className='text-sm text-gray-700 dark:text-gray-300'>
                      Markdown Support (マークダウン記法)
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Image Limits - FEATURE_PLANS.md */}
            <div className='border-b pb-4 dark:border-gray-700'>
              <h3 className='mb-3 text-sm font-semibold text-gray-700 dark:text-gray-300'>
                Image Limits
              </h3>
              <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                <div>
                  <label className='mb-1 block text-xs text-gray-600 dark:text-gray-400'>
                    Max Images per Post (画像枚数/投稿)
                  </label>
                  <input
                    type='number'
                    value={formData.limits.maxImagesPerPost as number}
                    onChange={e =>
                      handleLimitChange('maxImagesPerPost', parseInt(e.target.value) || 0)
                    }
                    min='0'
                    max='4'
                    className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                  />
                  <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>Bluesky max: 4</p>
                </div>

                <div>
                  <label className='mb-1 block text-xs text-gray-600 dark:text-gray-400'>
                    Max Image Size MB (画像サイズ上限)
                  </label>
                  <input
                    type='number'
                    value={formData.limits.maxImageSizeMb as number}
                    onChange={e =>
                      handleLimitChange('maxImageSizeMb', parseInt(e.target.value) || 0)
                    }
                    min='1'
                    className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                  />
                  <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                    AT Protocol max: 2MB
                  </p>
                </div>
              </div>
            </div>

            {/* Video Limits - FEATURE_PLANS.md */}
            <div className='border-b pb-4 dark:border-gray-700'>
              <h3 className='mb-3 text-sm font-semibold text-gray-700 dark:text-gray-300'>
                Video Limits
              </h3>
              <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                <div className='flex items-center'>
                  <label className='flex items-center gap-2'>
                    <input
                      type='checkbox'
                      checked={formData.limits.videoUpload as boolean}
                      onChange={e => handleLimitChange('videoUpload', e.target.checked)}
                      className='h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700'
                    />
                    <span className='text-sm text-gray-700 dark:text-gray-300'>
                      Video Upload Enabled (動画アップロード)
                    </span>
                  </label>
                </div>

                <div>
                  <label className='mb-1 block text-xs text-gray-600 dark:text-gray-400'>
                    Max Video Size MB (動画サイズ上限)
                  </label>
                  <input
                    type='number'
                    value={(formData.limits.maxVideoSizeMb as number | null) ?? 0}
                    onChange={e => {
                      const value = parseInt(e.target.value) || 0;
                      handleLimitChange('maxVideoSizeMb', value === 0 ? null : value);
                    }}
                    min='0'
                    className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                  />
                  <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                    0 = not available, 100MB typical
                  </p>
                </div>

                <div className='col-span-2'>
                  <label className='mb-1 block text-xs text-gray-600 dark:text-gray-400'>
                    Video Processing Minutes/Month (動画処理時間/月)
                  </label>
                  <input
                    type='number'
                    value={
                      (formData.limits.video_processing_minutes_per_month as number | null) ?? 0
                    }
                    onChange={e => {
                      const value = parseInt(e.target.value) || 0;
                      handleLimitChange(
                        'video_processing_minutes_per_month',
                        value === 0 ? null : value
                      );
                    }}
                    min='0'
                    className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                  />
                  <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>0 = not available</p>
                </div>
              </div>
            </div>

            {/* Other Limits */}
            <div>
              <h3 className='mb-3 text-sm font-semibold text-gray-700 dark:text-gray-300'>
                Other Limits
              </h3>
              <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                <div>
                  <label className='mb-1 block text-xs text-gray-600 dark:text-gray-400'>
                    Max Post Length
                  </label>
                  <input
                    type='number'
                    value={formData.limits.maxPostLength as number}
                    onChange={e =>
                      handleLimitChange('maxPostLength', parseInt(e.target.value) || 300)
                    }
                    min='1'
                    max='1000'
                    className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                  />
                  <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                    Characters (Bluesky max: 300)
                  </p>
                </div>

                <div>
                  <label className='mb-1 block text-xs text-gray-600 dark:text-gray-400'>
                    Storage (MB)
                  </label>
                  <input
                    type='number'
                    value={formData.limits.storage_mb as number}
                    onChange={e => handleLimitChange('storage_mb', parseInt(e.target.value) || 0)}
                    min='-1'
                    className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                  />
                  <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>-1 for unlimited</p>
                </div>

                <div>
                  <label className='mb-1 block text-xs text-gray-600 dark:text-gray-400'>
                    API Rate Limit (per hour)
                  </label>
                  <input
                    type='number'
                    value={formData.limits.api_rate_limit_per_hour as number}
                    onChange={e =>
                      handleLimitChange('api_rate_limit_per_hour', parseInt(e.target.value) || 0)
                    }
                    min='-1'
                    className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                  />
                  <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>-1 for unlimited</p>
                </div>

                <div>
                  <label className='mb-1 block text-xs text-gray-600 dark:text-gray-400'>
                    Data Retention (days)
                  </label>
                  <input
                    type='number'
                    value={formData.limits.data_retention_days as number}
                    onChange={e =>
                      handleLimitChange('data_retention_days', parseInt(e.target.value) || 0)
                    }
                    min='-1'
                    className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                  />
                  <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>-1 for unlimited</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features */}
        <div>
          <label className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'>
            Features (one per line)
          </label>
          <div className='space-y-3'>
            {SUPPORTED_LANGUAGES.map(lang => (
              <div key={lang}>
                <label className='mb-1 block text-xs text-gray-600 dark:text-gray-400'>
                  {lang.toUpperCase()}
                </label>
                <textarea
                  value={formData.features[lang].join('\n')}
                  onChange={e => handleFeatureChange(lang, e.target.value)}
                  rows={4}
                  className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                  placeholder={`Features in ${lang} (one per line)`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Display Settings */}
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'>
          <div>
            <label
              htmlFor='displayOrder'
              className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'
            >
              Display Order
            </label>
            <input
              type='number'
              id='displayOrder'
              value={formData.displayOrder}
              onChange={e => handleChange('displayOrder', parseInt(e.target.value) || 0)}
              min='0'
              className='w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white'
            />
          </div>

          <div className='flex items-end'>
            <label className='flex items-center gap-2'>
              <input
                type='checkbox'
                checked={formData.isActive}
                onChange={e => handleChange('isActive', e.target.checked)}
                className='h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700'
              />
              <span className='text-sm text-gray-700 dark:text-gray-300'>Active</span>
            </label>
          </div>

          <div className='flex items-end'>
            <label className='flex items-center gap-2'>
              <input
                type='checkbox'
                checked={formData.isVisible}
                onChange={e => handleChange('isVisible', e.target.checked)}
                className='h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700'
              />
              <span className='text-sm text-gray-700 dark:text-gray-300'>Visible</span>
            </label>
          </div>
        </div>

        {/* Submit */}
        <div className='flex gap-4'>
          <button
            type='submit'
            disabled={loading}
            className='flex-1 rounded-md bg-blue-600 px-6 py-3 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300 dark:disabled:bg-gray-700 dark:disabled:text-gray-500'
          >
            {loading ? 'Creating...' : 'Create Plan'}
          </button>
          <button
            type='button'
            onClick={() => navigate('/plans')}
            className='rounded-md bg-gray-200 px-6 py-3 text-gray-700 transition-colors hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreatePlanPage;
